import axios from 'axios';
import logger from '@next/core-logger';

const CORRELATION_HEADER_NAME = "x-next-correlation-id";
const SESSION_HEADER_NAME = "x-next-session-id";
const TIME_MACHINE_HEADER_NAME = "x-next-time-machine-date";
const TOKEN_CANCELLATION_FLAG = "TOKEN_CANCELLATION";

function IS_BROWSER() {
    return typeof window !== "undefined";
}
function getRegExValue(valueIn, name, pattern) {
    const re = new RegExp(name + pattern);
    const value = re.exec(valueIn);
    return value ? unescape(value[1]) : null;
}
function tryCatchLogStringifyError(error, correlationId) {
    if (axios.isAxiosError(error)) {
        try {
            logger.error(JSON.stringify(error.toJSON()), correlationId);
        }
        catch (e) {
            logger.error(`JSON.stringify(error.toJSON()) failed while ${error.message}`, correlationId);
        }
    }
    else {
        try {
            logger.error(JSON.stringify(error), correlationId);
        }
        catch (e) {
            logger.error(`JSON.stringify(error) failed while ${error.message}`, correlationId);
        }
    }
}

function createAxiosInstance({ PORT, REACT_APP_APP_URL }) {
    const axiosInstance = axios.create({});
    axiosInstance.defaults.baseURL = IS_BROWSER() ? REACT_APP_APP_URL : `http://localhost:${PORT}`;
    const setAxiosDefaultsFromNextRequest = (req) => {
        var _a;
        const nextVisitorCookie = ((_a = req.headers) === null || _a === void 0 ? void 0 : _a.cookie)
            ? getRegExValue(req.headers.cookie, "NextVisitor", "=([^;]+)")
            : undefined;
        axiosInstance.defaults.headers.common[SESSION_HEADER_NAME] = nextVisitorCookie
            ? getRegExValue(nextVisitorCookie, "LatestSessionID", "=(.*)&LatestSessionTimestamp")
            : "";
        if (req.headers[CORRELATION_HEADER_NAME]) {
            axiosInstance.defaults.headers.common[CORRELATION_HEADER_NAME] = req.headers[CORRELATION_HEADER_NAME];
        }
    };
    axiosInstance.interceptors.request.use(config => {
        setAxiosDefaultsFromNextRequest(config);
        return config;
    }, error => {
        tryCatchLogStringifyError(error, "axios-request-error");
        return Promise.reject(error);
    });
    axiosInstance.interceptors.response.use(response => {
        return response;
    }, error => {
        const badStatus = error.response &&
            error.response.status &&
            (error.response.status === 404 || error.response.status === 304);
        const userCancelledRequest = (error === null || error === void 0 ? void 0 : error.message) === TOKEN_CANCELLATION_FLAG;
        if (badStatus || userCancelledRequest) {
            return Promise.reject(error);
        }
        tryCatchLogStringifyError(error, "axios-response-error");
        return Promise.reject(error);
    });
    return axiosInstance;
}

function createAxiosXhrInstance() {
    const axiosInstance = axios.create({});
    axiosInstance.interceptors.request.use(config => {
        logger.debug(`URL: ${config.url}, HEADERS: ${JSON.stringify(config.headers)}`, config.headers[CORRELATION_HEADER_NAME]);
        if (config.headers[TIME_MACHINE_HEADER_NAME]) {
            logger.info(config.url, config.headers[TIME_MACHINE_HEADER_NAME]);
        }
        return config;
    });
    axiosInstance.interceptors.response.use(response => {
        logger.debug(`${response.config.url} - ${response.status}`, response.headers[CORRELATION_HEADER_NAME]);
        if (response.headers[TIME_MACHINE_HEADER_NAME]) {
            logger.info(`${response.config.url} - ${response.status}`, response.headers[TIME_MACHINE_HEADER_NAME]);
        }
        return response;
    }, error => {
        tryCatchLogStringifyError(error, "axios-response-error-server");
        return Promise.reject(error);
    });
    return axiosInstance;
}

export { CORRELATION_HEADER_NAME, IS_BROWSER, SESSION_HEADER_NAME, TIME_MACHINE_HEADER_NAME, TOKEN_CANCELLATION_FLAG, createAxiosInstance, createAxiosXhrInstance, getRegExValue, tryCatchLogStringifyError };
